import { PlusOutlined } from "@ant-design/icons";
import { Button, Dropdown, Tooltip } from "antd";
import { useApplicationContext } from "src/hooks/useApplication";
import { useDeepLinks } from "src/hooks/useDeepLinks";
import { ItemTypeIcon } from "./icons/ItemTypeIcon";
import { ItemType } from "./useLibrary";
import { DeepLinkType } from "pages/api/deep-links/types";
import {
  DEFAULT_COLUMN_COUNT,
  DEFAULT_ROW_HEIGHT,
  DEFAULT_SPACING,
} from "pages/[orgId]/dashboards/DashboardViewer";
import { theme } from "antd";
import { useSpacesQuery } from "pages/[orgId]/space/useSpacesQuery";
import { useCreateTopic } from "src/hooks/useCreateTopic";

interface CreateButtonProps {
  orgId: string;
  showTopic?: boolean;
  placement?: "bottomRight" | "bottomLeft" | "topRight" | "topLeft";
}

export function CreateButton({
  orgId,
  showTopic = false,
  placement = "bottomRight",
}: CreateButtonProps) {
  const app = useApplicationContext();
  const boards = useDeepLinks(DeepLinkType.BOARD);
  const { token } = theme.useToken();
  const query = useSpacesQuery();
  const { createTopic } = useCreateTopic();

  const createItems = getCreateItems({
    app,
    boards,
    query,
    showTopic,
    createTopic,
  });

  return (
    <Dropdown
      menu={{ items: createItems }}
      trigger={["click"]}
      placement={placement}
    >
      <Button
        type="primary"
        icon={<PlusOutlined />}
        style={{
          borderRadius: "50%",
          height: 36,
          width: 36,
          justifyContent: "center",
          marginRight: 24,
          gap: 4,
          boxShadow: "0 2px 0 rgba(0, 0, 0, 0.02)",
        }}
      ></Button>
    </Dropdown>
  );
}

export function getCreateItems({
  app,
  boards,
  query,
  showTopic = false,
  createTopic,
}: {
  app: ReturnType<typeof useApplicationContext>;
  boards: ReturnType<typeof useDeepLinks>;
  query: ReturnType<typeof useSpacesQuery>;
  showTopic?: boolean;
  createTopic?: ReturnType<typeof useCreateTopic>["createTopic"];
}) {
  const createItems = [
    {
      key: "space",
      label: (
        <Tooltip title="Create a new space to organize your content">
          <div style={{ width: "100%" }}>New space</div>
        </Tooltip>
      ),
      icon: <ItemTypeIcon type={ItemType.SPACES} style={{ marginRight: 6 }} />,
      onClick: () => {
        if (app.org_id) {
          query.upsertSpace.mutate(
            { name: "New Space", scope: "private" },
            {
              onSuccess: (space) => {
                app.appendTab(
                  space.name || "New Space",
                  `/${app.org_id}/space/${space.uuid}?edit=1`,
                  { open: true }
                );
              },
            }
          );
        }
      },
    },
    {
      key: "board",
      label: (
        <Tooltip title="Create a new dashboard to visualize your data">
          <div style={{ width: "100%" }}>New board</div>
        </Tooltip>
      ),
      icon: <ItemTypeIcon type={ItemType.BOARDS} style={{ marginRight: 6 }} />,
      onClick: () => {
        if (app.org_id) {
          boards.create
            .mutateAsync({
              state: {
                data: {},
                layout: [],
                colorTheme: "standard",
                columnCount: DEFAULT_COLUMN_COUNT,
                rowHeight: DEFAULT_ROW_HEIGHT,
                spacing: DEFAULT_SPACING,
              },
              name: "New Board",
              description: "",
              scope: "private",
            })
            .then((link) => {
              app.appendTab("New Board", `/${app.org_id}/boards/${link.id}`, {
                open: true,
              });
            });
        }
      },
    },
    {
      key: "query",
      label: (
        <Tooltip title="Create a new query to explore your data">
          <div style={{ width: "100%" }}>New query</div>
        </Tooltip>
      ),
      icon: <ItemTypeIcon type={ItemType.QUERIES} style={{ marginRight: 6 }} />,
      onClick: () => {
        if (app.org_id) {
          app.appendTab("Empty query", `/${app.org_id}/explore`, {
            open: true,
          });
        }
      },
    },
  ];

  if (showTopic && app.isSuperAdmin && createTopic) {
    createItems.push({
      key: "topic",
      label: (
        <Tooltip title="Create a new topic to gain visibility into your data">
          <div style={{ width: "100%" }}>New topic</div>
        </Tooltip>
      ),
      icon: <ItemTypeIcon type={ItemType.TOPICS} style={{ marginRight: 6 }} />,
      onClick: () => createTopic({ name: "New Topic" }),
    });
  }
  return createItems;
}
